import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  School, LogOut, Copy, Check, Plus, Trash2, BookOpen, GraduationCap,
  Users, Globe, ChevronDown, ChevronUp, RefreshCw, X, Loader2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useBasePath } from '@/contexts/PortalContext';
import TerrainBackground from '@/components/assessment/TerrainBackground';
import AdminLayoutWrapper from '@/layouts/AdminLayout';

// ─── Types ─────────────────────────────────────────────────────────────────

interface SchoolRecord {
  id: string;
  name: string;
  city: string | null;
  state_province: string | null;
  country: string;
  website_url: string | null;
  school_code: string;
}

interface SchoolMembership {
  role: 'admin' | 'counselor' | 'student';
  school_id: string;
  school: SchoolRecord;
}

interface Course {
  id: string;
  name: string;
  category: string;
  grade_levels: string[];
  credits: number | null;
  description: string | null;
  active: boolean;
}

interface Club {
  id: string;
  name: string;
  type: string;
  commitment: string;
  description: string | null;
  active: boolean;
}

// ─── Zod schemas ───────────────────────────────────────────────────────────

const createSchoolSchema = z.object({
  name: z.string().min(2, 'School name is required').max(120),
  city: z.string().max(80).optional(),
  state_province: z.string().max(80).optional(),
  country: z.string().length(2, 'Use 2-letter country code').default('US'),
  website_url: z.string().url('Enter a valid URL').or(z.literal('')).optional(),
});
type CreateSchoolData = z.infer<typeof createSchoolSchema>;

const courseSchema = z.object({
  name: z.string().min(1, 'Required').max(120),
  category: z.enum(['core', 'elective', 'ap-honors', 'cte', 'other']),
  grade_levels: z.string().optional(), // comma-separated, parsed on submit
  credits: z.coerce.number().min(0).max(99).optional().or(z.literal('')),
  description: z.string().max(500).optional(),
});
type CourseFormData = z.infer<typeof courseSchema>;

const clubSchema = z.object({
  name: z.string().min(1, 'Required').max(120),
  type: z.enum(['academic', 'leadership', 'community', 'creative', 'competition', 'professional', 'sports', 'other']),
  commitment: z.enum(['low', 'medium', 'high']),
  description: z.string().max(500).optional(),
});
type ClubFormData = z.infer<typeof clubSchema>;

// ─── Small helpers ─────────────────────────────────────────────────────────

const inputCls = 'w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary';
const selectCls = inputCls;
const labelCls = 'text-xs font-medium text-foreground';
const errorCls = 'text-xs text-destructive';

function FieldError({ message }: { message?: string }) {
  return message ? <p role="alert" className={errorCls}>{message}</p> : null;
}

// ─── Sub-components ────────────────────────────────────────────────────────

function SchoolCodeCard({ school, canManage }: { school: SchoolRecord; canManage: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(school.school_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="glass-card p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-lg text-foreground">{school.name}</h2>
          <p className="text-xs text-muted-foreground">
            {[school.city, school.state_province, school.country].filter(Boolean).join(', ')}
          </p>
        </div>
        {school.website_url && (
          <a href={school.website_url} target="_blank" rel="noopener noreferrer"
            className="text-xs text-primary flex items-center gap-1 hover:underline shrink-0"
            aria-label={`Visit ${school.name} website`}>
            <Globe className="w-3 h-3" aria-hidden="true" /> Website
          </a>
        )}
      </div>
      {canManage && (
        <div className="bg-secondary/60 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Student Join Code</p>
            <p className="font-mono text-xl font-bold text-primary tracking-widest mt-0.5">{school.school_code}</p>
          </div>
          <button
            onClick={copy}
            aria-label={copied ? 'Copied!' : 'Copy school code'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-primary" aria-hidden="true" /> : <Copy className="w-3.5 h-3.5" aria-hidden="true" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Add Course Form ────────────────────────────────────────────────────────

function AddCourseForm({ schoolId, onDone }: { schoolId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<CourseFormData>({
    resolver: zodResolver(courseSchema),
    defaultValues: { category: 'elective', credits: '' },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: CourseFormData) => {
      const grade_levels = data.grade_levels
        ? data.grade_levels.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      const { error } = await supabase.from('school_courses' as never).insert({
        school_id: schoolId,
        name: data.name,
        category: data.category,
        grade_levels,
        credits: data.credits === '' ? null : Number(data.credits),
        description: data.description || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school_courses', schoolId] });
      reset();
      onDone();
    },
  });

  return (
    <form onSubmit={handleSubmit(d => mutate(d))} aria-label="Add course form" className="glass-card p-4 space-y-3 border-primary/30">
      <h3 className="text-sm font-semibold text-foreground">Add Course</h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1">
          <label htmlFor="course-name" className={labelCls}>Course Name *</label>
          <input id="course-name" {...register('name')} className={inputCls} placeholder="AP Computer Science" />
          <FieldError message={errors.name?.message} />
        </div>
        <div className="space-y-1">
          <label htmlFor="course-category" className={labelCls}>Category</label>
          <select id="course-category" {...register('category')} className={selectCls}>
            <option value="core">Core</option>
            <option value="elective">Elective</option>
            <option value="ap-honors">AP / Honors</option>
            <option value="cte">CTE</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="course-credits" className={labelCls}>Credits</label>
          <input id="course-credits" type="number" step="0.5" min="0" {...register('credits')} className={inputCls} placeholder="1.0" />
        </div>
        <div className="col-span-2 space-y-1">
          <label htmlFor="course-grades" className={labelCls}>Grade Levels (comma-separated)</label>
          <input id="course-grades" {...register('grade_levels')} className={inputCls} placeholder="9, 10, 11, 12" />
        </div>
        <div className="col-span-2 space-y-1">
          <label htmlFor="course-desc" className={labelCls}>Description (optional)</label>
          <textarea id="course-desc" {...register('description')} rows={2} className={inputCls} placeholder="Brief description..." />
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={isPending} aria-busy={isPending}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50">
          {isPending ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> : <Plus className="w-3 h-3" aria-hidden="true" />}
          {isPending ? 'Adding...' : 'Add Course'}
        </button>
        <button type="button" onClick={onDone}
          className="flex items-center gap-1 px-4 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground">
          <X className="w-3 h-3" aria-hidden="true" /> Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Add Club Form ──────────────────────────────────────────────────────────

function AddClubForm({ schoolId, onDone }: { schoolId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ClubFormData>({
    resolver: zodResolver(clubSchema),
    defaultValues: { type: 'academic', commitment: 'medium' },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: ClubFormData) => {
      const { error } = await supabase.from('school_clubs' as never).insert({
        school_id: schoolId,
        name: data.name,
        type: data.type,
        commitment: data.commitment,
        description: data.description || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school_clubs', schoolId] });
      reset();
      onDone();
    },
  });

  return (
    <form onSubmit={handleSubmit(d => mutate(d))} aria-label="Add club form" className="glass-card p-4 space-y-3 border-primary/30">
      <h3 className="text-sm font-semibold text-foreground">Add Club / Activity</h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1">
          <label htmlFor="club-name" className={labelCls}>Name *</label>
          <input id="club-name" {...register('name')} className={inputCls} placeholder="Robotics Club" />
          <FieldError message={errors.name?.message} />
        </div>
        <div className="space-y-1">
          <label htmlFor="club-type" className={labelCls}>Type</label>
          <select id="club-type" {...register('type')} className={selectCls}>
            <option value="academic">Academic</option>
            <option value="leadership">Leadership</option>
            <option value="community">Community Service</option>
            <option value="creative">Creative / Arts</option>
            <option value="competition">Competition</option>
            <option value="professional">Professional</option>
            <option value="sports">Sports</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="club-commitment" className={labelCls}>Commitment Level</label>
          <select id="club-commitment" {...register('commitment')} className={selectCls}>
            <option value="low">Low (1–2 hrs/week)</option>
            <option value="medium">Medium (3–5 hrs/week)</option>
            <option value="high">High (6+ hrs/week)</option>
          </select>
        </div>
        <div className="col-span-2 space-y-1">
          <label htmlFor="club-desc" className={labelCls}>Description (optional)</label>
          <textarea id="club-desc" {...register('description')} rows={2} className={inputCls} placeholder="Brief description..." />
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={isPending} aria-busy={isPending}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50">
          {isPending ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> : <Plus className="w-3 h-3" aria-hidden="true" />}
          {isPending ? 'Adding...' : 'Add Club'}
        </button>
        <button type="button" onClick={onDone}
          className="flex items-center gap-1 px-4 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground">
          <X className="w-3 h-3" aria-hidden="true" /> Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Courses Tab ────────────────────────────────────────────────────────────

function CoursesTab({ schoolId, canManage }: { schoolId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data: courses = [], isLoading } = useQuery<Course[]>({
    queryKey: ['school_courses', schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('school_courses' as never)
        .select('id, name, category, grade_levels, credits, description, active')
        .eq('school_id', schoolId)
        .order('name');
      if (error) throw error;
      return (data ?? []) as Course[];
    },
  });

  const { mutate: remove } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('school_courses' as never).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school_courses', schoolId] }),
  });

  const CATEGORY_LABEL: Record<string, string> = {
    core: 'Core', elective: 'Elective', 'ap-honors': 'AP/Honors', cte: 'CTE', other: 'Other',
  };

  return (
    <section aria-label="Courses catalog">
      {canManage && (
        <div className="mb-4 flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">{courses.length} course{courses.length !== 1 ? 's' : ''}</p>
          {!adding && (
            <button onClick={() => setAdding(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium">
              <Plus className="w-3.5 h-3.5" aria-hidden="true" /> Add Course
            </button>
          )}
        </div>
      )}

      <AnimatePresence>
        {adding && (
          <motion.div key="add-course-form" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} className="mb-4 overflow-hidden">
            <AddCourseForm schoolId={schoolId} onDone={() => setAdding(false)} />
          </motion.div>
        )}
      </AnimatePresence>

      {isLoading && (
        <div className="py-8 flex justify-center" aria-busy="true" aria-label="Loading courses">
          <Loader2 className="w-5 h-5 animate-spin text-primary" aria-hidden="true" />
        </div>
      )}

      {!isLoading && courses.length === 0 && (
        <div className="glass-card p-6 text-center text-sm text-muted-foreground">
          No courses yet. {canManage ? 'Add your first course above.' : ''}
        </div>
      )}

      {!isLoading && courses.length > 0 && (
        <ul className="space-y-2" aria-label="Course list">
          {courses.map(c => (
            <motion.li key={c.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="glass-card px-4 py-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">{c.name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-secondary text-muted-foreground">
                    {CATEGORY_LABEL[c.category] ?? c.category}
                  </span>
                  {c.credits != null && (
                    <span className="text-[10px] text-muted-foreground">{c.credits} cr</span>
                  )}
                  {c.grade_levels?.length > 0 && (
                    <span className="text-[10px] text-muted-foreground">
                      Gr {c.grade_levels.join(', ')}
                    </span>
                  )}
                </div>
                {c.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{c.description}</p>}
              </div>
              {canManage && (
                <button onClick={() => remove(c.id)} aria-label={`Delete course ${c.name}`}
                  className="shrink-0 text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              )}
            </motion.li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Clubs Tab ──────────────────────────────────────────────────────────────

function ClubsTab({ schoolId, canManage }: { schoolId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data: clubs = [], isLoading } = useQuery<Club[]>({
    queryKey: ['school_clubs', schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('school_clubs' as never)
        .select('id, name, type, commitment, description, active')
        .eq('school_id', schoolId)
        .order('name');
      if (error) throw error;
      return (data ?? []) as Club[];
    },
  });

  const { mutate: remove } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('school_clubs' as never).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school_clubs', schoolId] }),
  });

  const COMMITMENT_LABEL: Record<string, string> = { low: 'Low', medium: 'Medium', high: 'High' };
  const TYPE_LABEL: Record<string, string> = {
    academic: 'Academic', leadership: 'Leadership', community: 'Community',
    creative: 'Creative', competition: 'Competition', professional: 'Professional',
    sports: 'Sports', other: 'Other',
  };

  return (
    <section aria-label="Clubs catalog">
      {canManage && (
        <div className="mb-4 flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">{clubs.length} club{clubs.length !== 1 ? 's' : ''}</p>
          {!adding && (
            <button onClick={() => setAdding(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium">
              <Plus className="w-3.5 h-3.5" aria-hidden="true" /> Add Club
            </button>
          )}
        </div>
      )}

      <AnimatePresence>
        {adding && (
          <motion.div key="add-club-form" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} className="mb-4 overflow-hidden">
            <AddClubForm schoolId={schoolId} onDone={() => setAdding(false)} />
          </motion.div>
        )}
      </AnimatePresence>

      {isLoading && (
        <div className="py-8 flex justify-center" aria-busy="true" aria-label="Loading clubs">
          <Loader2 className="w-5 h-5 animate-spin text-primary" aria-hidden="true" />
        </div>
      )}

      {!isLoading && clubs.length === 0 && (
        <div className="glass-card p-6 text-center text-sm text-muted-foreground">
          No clubs yet. {canManage ? 'Add your first club above.' : ''}
        </div>
      )}

      {!isLoading && clubs.length > 0 && (
        <ul className="space-y-2" aria-label="Club list">
          {clubs.map(c => (
            <motion.li key={c.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="glass-card px-4 py-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">{c.name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-secondary text-muted-foreground">
                    {TYPE_LABEL[c.type] ?? c.type}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{COMMITMENT_LABEL[c.commitment]} commitment</span>
                </div>
                {c.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{c.description}</p>}
              </div>
              {canManage && (
                <button onClick={() => remove(c.id)} aria-label={`Delete club ${c.name}`}
                  className="shrink-0 text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              )}
            </motion.li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Students Tab (CSV Upload + Roster) ─────────────────────────────────────

function StudentsTab({ schoolId }: { schoolId: string }) {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data: students, isLoading } = useQuery({
    queryKey: ['enrolled_students', schoolId],
    queryFn: async () => {
      const { data } = await supabase
        .from('enrolled_students')
        .select('id, student_id, first_name, last_name, grade, email, user_id')
        .eq('school_id', schoolId)
        .order('last_name');
      return data ?? [];
    },
  });

  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { setUploadResult('CSV must have a header row and at least one student'); setUploading(false); return; }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const studentIdIdx = headers.findIndex(h => h.includes('student') && h.includes('id'));
      const firstIdx = headers.findIndex(h => h.includes('first'));
      const lastIdx = headers.findIndex(h => h.includes('last'));
      const gradeIdx = headers.findIndex(h => h.includes('grade'));
      const emailIdx = headers.findIndex(h => h.includes('email'));

      if (studentIdIdx === -1 || firstIdx === -1 || lastIdx === -1) {
        setUploadResult('CSV must have columns: student_id (or Student ID), first_name, last_name');
        setUploading(false);
        return;
      }

      const students = lines.slice(1).map(line => {
        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        return {
          student_id: cols[studentIdIdx] || '',
          first_name: cols[firstIdx] || '',
          last_name: cols[lastIdx] || '',
          grade: gradeIdx >= 0 ? cols[gradeIdx] : null,
          email: emailIdx >= 0 ? cols[emailIdx] : null,
        };
      }).filter(s => s.student_id && s.first_name && s.last_name);

      if (students.length === 0) { setUploadResult('No valid students found in CSV'); setUploading(false); return; }

      const { data, error } = await supabase.rpc('import_student_roster' as never, {
        p_school_id: schoolId,
        p_students: students,
      });

      if (error) { setUploadResult(`Error: ${error.message}`); }
      else { setUploadResult(`Successfully imported ${(data as { imported: number })?.imported ?? students.length} students`); qc.invalidateQueries({ queryKey: ['enrolled_students'] }); }
    } catch (err) {
      setUploadResult('Failed to read CSV file');
    }
    setUploading(false);
  };

  return (
    <section aria-label="Student roster">
      <div className="space-y-4">
        <div className="glass-card p-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Upload Student Roster</h3>
          <p className="text-xs text-muted-foreground">
            Upload a CSV file with columns: <code className="text-primary">student_id</code>, <code className="text-primary">first_name</code>, <code className="text-primary">last_name</code>, <code className="text-primary">grade</code> (optional), <code className="text-primary">email</code> (optional)
          </p>
          <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary/10 text-primary text-xs font-semibold cursor-pointer hover:bg-primary/20 transition-colors w-fit">
            <Plus className="w-3.5 h-3.5" />
            {uploading ? 'Uploading...' : 'Choose CSV File'}
            <input type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" disabled={uploading} />
          </label>
          {uploadResult && (
            <p className={`text-xs ${uploadResult.startsWith('Error') || uploadResult.startsWith('Failed') || uploadResult.startsWith('CSV') || uploadResult.startsWith('No') ? 'text-destructive' : 'text-primary'}`}>
              {uploadResult}
            </p>
          )}
        </div>

        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">
              Enrolled Students {students ? `(${students.length})` : ''}
            </h3>
          </div>

          {isLoading && <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />}

          {!isLoading && (!students || students.length === 0) && (
            <p className="text-xs text-muted-foreground text-center py-4">No students uploaded yet. Upload a CSV to get started.</p>
          )}

          {students && students.length > 0 && (
            <ul className="space-y-1 max-h-80 overflow-y-auto">
              {students.map((s: { id: string; student_id: string; first_name: string; last_name: string; grade: string | null; user_id: string | null }) => (
                <li key={s.id} className="flex items-center gap-3 px-3 py-2 rounded-lg text-xs hover:bg-secondary/50">
                  <span className="font-mono text-muted-foreground w-20 shrink-0">{s.student_id}</span>
                  <span className="flex-1 font-medium text-foreground">{s.first_name} {s.last_name}</span>
                  {s.grade && <span className="text-muted-foreground">{s.grade}</span>}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${s.user_id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                    {s.user_id ? 'Linked' : 'Pending'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Register School Form ───────────────────────────────────────────────────

function RegisterSchoolForm({ onCreated }: { onCreated: () => void }) {
  const { register, handleSubmit, formState: { errors } } = useForm<CreateSchoolData>({
    resolver: zodResolver(createSchoolSchema),
    defaultValues: { country: 'US' },
  });
  const [serverError, setServerError] = useState('');
  const [creating, setCreating] = useState(false);

  const onSubmit = async (data: CreateSchoolData) => {
    setCreating(true);
    setServerError('');
    const { error } = await supabase.rpc('create_school' as never, {
      p_name: data.name,
      p_city: data.city || null,
      p_state_province: data.state_province || null,
      p_country: data.country,
      p_website_url: data.website_url || null,
    });
    setCreating(false);
    if (error) { setServerError(error.message); return; }
    onCreated();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg mx-auto space-y-6">
      <div className="text-center space-y-1">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/15 flex items-center justify-center">
          <School className="w-7 h-7 text-primary" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-display font-bold text-foreground">Register Your School</h1>
        <p className="text-sm text-muted-foreground">Create a school profile so students can get personalized career paths based on your actual course catalog.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} aria-label="Register school form" className="glass-card p-6 space-y-4">
        <div className="space-y-1">
          <label htmlFor="school-name" className={labelCls}>School Name *</label>
          <input id="school-name" {...register('name')} className={inputCls} placeholder="Riverside High School" />
          <FieldError message={errors.name?.message} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="school-city" className={labelCls}>City</label>
            <input id="school-city" {...register('city')} className={inputCls} placeholder="Toronto" />
          </div>
          <div className="space-y-1">
            <label htmlFor="school-state" className={labelCls}>State / Province</label>
            <input id="school-state" {...register('state_province')} className={inputCls} placeholder="ON" />
          </div>
          <div className="space-y-1">
            <label htmlFor="school-country" className={labelCls}>Country Code *</label>
            <input id="school-country" {...register('country')} className={inputCls} placeholder="US" maxLength={2} />
            <FieldError message={errors.country?.message} />
          </div>
          <div className="space-y-1">
            <label htmlFor="school-website" className={labelCls}>Website URL</label>
            <input id="school-website" type="url" {...register('website_url')} className={inputCls} placeholder="https://school.edu" />
            <FieldError message={errors.website_url?.message} />
          </div>
        </div>

        {serverError && (
          <p role="alert" aria-live="assertive" className={errorCls}>{serverError}</p>
        )}

        <button type="submit" disabled={creating} aria-busy={creating}
          className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
          {creating && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
          {creating ? 'Creating...' : 'Create School'}
        </button>
      </form>
    </motion.div>
  );
}

// ─── Auto Import Panel ──────────────────────────────────────────────────────

function AutoImportPanel({ school, schoolId }: { school: SchoolRecord; schoolId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ courses: Course[]; clubs: Club[] } | null>(null);
  const [importError, setImportError] = useState('');

  const runImport = async () => {
    if (!school.website_url) return;
    setImporting(true);
    setImportError('');
    setImportResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('scrape-school-catalog', {
        body: { schoolId, websiteUrl: school.website_url, schoolName: school.name },
      });
      if (error) throw error;
      setImportResult(data as { courses: Course[]; clubs: Club[] });
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    if (!importResult) return;
    setImporting(true);
    try {
      if (importResult.courses.length > 0) {
        await supabase.from('school_courses' as never).insert(
          importResult.courses.map(c => ({ ...c, school_id: schoolId, id: undefined }))
        );
      }
      if (importResult.clubs.length > 0) {
        await supabase.from('school_clubs' as never).insert(
          importResult.clubs.map(c => ({ ...c, school_id: schoolId, id: undefined }))
        );
      }
      qc.invalidateQueries({ queryKey: ['school_courses', schoolId] });
      qc.invalidateQueries({ queryKey: ['school_clubs', schoolId] });
      setImportResult(null);
      setOpen(false);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Failed to save imported data');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="glass-card overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls="auto-import-panel"
        className="w-full px-5 py-3 flex items-center justify-between text-sm font-medium text-foreground hover:bg-secondary/40 transition-colors"
      >
        <span className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-primary" aria-hidden="true" />
          Auto-import from school website
        </span>
        {open ? <ChevronUp className="w-4 h-4" aria-hidden="true" /> : <ChevronDown className="w-4 h-4" aria-hidden="true" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div id="auto-import-panel" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-t border-border/40">
            <div className="p-5 space-y-3">
              {!school.website_url && (
                <p className="text-xs text-muted-foreground">No website URL set for this school. Add one first.</p>
              )}
              {school.website_url && (
                <>
                  <p className="text-xs text-muted-foreground">
                    TruePath will scan <span className="text-foreground">{school.website_url}</span> and extract courses and clubs using AI. You'll review results before they're saved.
                  </p>
                  {!importResult && (
                    <button onClick={runImport} disabled={importing}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary border border-border text-sm text-foreground hover:border-primary/50 transition-colors disabled:opacity-50">
                      {importing
                        ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Scanning...</>
                        : <><RefreshCw className="w-4 h-4" aria-hidden="true" /> Start Import</>}
                    </button>
                  )}
                  {importError && <p role="alert" className={errorCls}>{importError}</p>}
                  {importResult && (
                    <div className="space-y-3">
                      <p className="text-xs text-foreground font-medium">
                        Found {importResult.courses.length} courses and {importResult.clubs.length} clubs. Confirm to save.
                      </p>
                      <div className="flex gap-2">
                        <button onClick={confirmImport} disabled={importing}
                          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50">
                          <Check className="w-3 h-3" aria-hidden="true" /> Confirm & Save
                        </button>
                        <button onClick={() => setImportResult(null)}
                          className="flex items-center gap-1 px-4 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground">
                          <X className="w-3 h-3" aria-hidden="true" /> Discard
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

const SchoolAdmin = () => {
  const { user, signOut } = useAuth();
  const basePath = useBasePath();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'courses' | 'clubs' | 'students'>('courses');

  const { data: membership, isLoading, refetch } = useQuery<SchoolMembership | null>({
    queryKey: ['school_membership', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('school_users' as never)
        .select('role, school_id, school:schools(id, name, city, state_province, country, website_url, school_code)')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as SchoolMembership | null;
    },
    enabled: !!user?.id,
  });

  const canManage = membership?.role === 'admin' || membership?.role === 'counselor';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-journey-atmosphere flex items-center justify-center" aria-busy="true" aria-label="Loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  }

  if (!membership) {
    return (
      <div className="min-h-screen bg-journey-atmosphere relative overflow-hidden">
        <TerrainBackground count={4} />
        <header className="px-6 py-4 flex items-center justify-between border-b border-border/30 bg-background/80 backdrop-blur-md sticky top-0 z-20">
          <Link to={basePath || '/'} className="text-primary font-display text-lg font-bold">TruePath</Link>
          <button onClick={signOut} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-10 relative z-10 space-y-6">
          <RegisterSchoolForm onCreated={() => { qc.invalidateQueries({ queryKey: ['school_membership'] }); refetch(); }} />
        </main>
      </div>
    );
  }

  return (
    <AdminLayoutWrapper schoolId={membership.school_id} schoolName={membership.school.name}>
      <div className="space-y-6">
        <SchoolCodeCard school={membership.school} canManage={canManage} />

        {canManage && <AutoImportPanel school={membership.school} schoolId={membership.school_id} />}

        <div role="tablist" aria-label="Catalog" className="flex gap-1 mb-4 bg-secondary/40 rounded-xl p-1">
          {(['courses', 'clubs', 'students'] as const).map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              aria-controls={`${tab}-panel`}
              id={`tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                activeTab === tab ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'courses' ? <BookOpen className="w-3.5 h-3.5" /> : tab === 'clubs' ? <Users className="w-3.5 h-3.5" /> : <GraduationCap className="w-3.5 h-3.5" />}
              {tab === 'courses' ? 'Courses' : tab === 'clubs' ? 'Clubs & Activities' : 'Students'}
            </button>
          ))}
        </div>

        <div role="tabpanel" id="courses-panel" hidden={activeTab !== 'courses'}>
          <CoursesTab schoolId={membership.school_id} canManage={canManage} />
        </div>
        <div role="tabpanel" id="clubs-panel" hidden={activeTab !== 'clubs'}>
          <ClubsTab schoolId={membership.school_id} canManage={canManage} />
        </div>
        {canManage && (
          <div role="tabpanel" id="students-panel" hidden={activeTab !== 'students'}>
            <StudentsTab schoolId={membership.school_id} />
          </div>
        )}
      </div>
    </AdminLayoutWrapper>
  );
};

export default SchoolAdmin;
